import clsx from "clsx";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import SignatureCanvas from "react-signature-canvas";

import { SignatureResponse } from "@smart/bridge-types-basic";
import {
  Button,
  SegmentedControl,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";

import { getUITranslation } from "../../../utils";
import { collectSignatureContext } from "../collect-signature-context";

const pngBase64FromPad = (pad: SignatureCanvas): string => {
  const dataUrl = pad.toDataURL("image/png");
  return dataUrl.split(",")[1] ?? "";
};

type SignatureMode = "draw" | "type";

type ElectronicSignatureFieldProps = {
  id: string;
  label?: string | null;
  value: SignatureResponse | null;
  disabled?: boolean;
  onChange: (value: SignatureResponse | null) => void;
  onBlur: () => void;
  selectedLanguage?: string;
};

export const ElectronicSignatureField = ({
  id,
  label,
  value,
  disabled,
  onChange,
  onBlur,
  selectedLanguage,
}: ElectronicSignatureFieldProps) => {
  const sigRef = useRef<SignatureCanvas>(null);
  const lastValueRef = useRef<SignatureResponse | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<SignatureMode>("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const [canvasSize, setCanvasSize] = useState({ width: 785, height: 150 });

  const isReadonly = Boolean(disabled);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return () => {};
    }

    const measure = () => {
      const w = Math.max(280, el.clientWidth);
      const h = 150;
      setCanvasSize({ width: w, height: h });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, []);

  const isSameValue =
    JSON.stringify(value) === JSON.stringify(lastValueRef.current);

  useEffect(() => {
    if (isSameValue) return;
    if (!value) {
      setMode("draw");
      setTypedSignature("");
      return;
    }
    setMode(value.mode);
    setTypedSignature(value.mode === "type" ? (value.text ?? "") : "");
  }, [value]);

  useEffect(() => {
    const pad = sigRef.current;
    if (!pad) return;
    if (isSameValue) return;

    if (!value || value.mode !== "draw" || !value.data) {
      pad.clear();
      return;
    }
    pad.clear();
    pad.fromDataURL(`data:image/png;base64,${value.data}`, { ratio: 1 });
  }, [value, mode]);

  const clearSignature = useCallback(() => {
    lastValueRef.current = null;
    onChange(null);
    onBlur();
  }, [onChange, onBlur]);

  const onSignatureDrawDone = useCallback(() => {
    const pad = sigRef.current;
    if (!pad || pad.isEmpty()) {
      clearSignature();
      return;
    }
    const data = pngBase64FromPad(pad);
    if (!data) {
      clearSignature();
      return;
    }
    const response: SignatureResponse = {
      mode: "draw",
      data,
      context: collectSignatureContext(),
    };
    lastValueRef.current = response;
    onChange(response);
    onBlur();
  }, [clearSignature, onChange, sigRef]);

  const saveTypedSignature = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        clearSignature();
        return;
      }
      const response: SignatureResponse = {
        mode: "type",
        text: trimmed,
        context: collectSignatureContext(),
      };
      lastValueRef.current = response;
      onChange(response);
      onBlur();
    },
    [clearSignature, onChange],
  );

  const handleClear = () => {
    if (isReadonly) return;
    sigRef.current?.clear();
    setTypedSignature("");
    clearSignature();
  };

  const handleModeChange = (next: SignatureMode) => {
    if (isReadonly || next === mode) return;
    sigRef.current?.clear();
    setTypedSignature("");
    clearSignature();
    setMode(next);
  };

  const getSegmentedControlIndex = () => {
    if (isReadonly) return -1;
    if (mode === "draw") return 0;
    return 1;
  };

  const signatureDrawn = value?.mode === "draw" && Boolean(value.data?.length);
  const showDrawMode = mode === "draw";

  return (
    <div className="flex flex-col gap-small w-full">
      <div className="flex flex-row items-center justify-between gap-small flex-wrap">
        <SegmentedControl
          branded
          index={getSegmentedControlIndex()}
          items={[
            {
              label: getUITranslation("signatureDraw", selectedLanguage),
              disabled: isReadonly,
              onClick: () => handleModeChange("draw"),
            },
            {
              label: getUITranslation("signatureType", selectedLanguage),
              disabled: isReadonly,
              onClick: () => handleModeChange("type"),
            },
          ]}
        />
        {showDrawMode && (
          <Button
            type="button"
            variant="secondarySubtle"
            size="small"
            branded
            text={getUITranslation("signatureClear", selectedLanguage)}
            disabled={isReadonly || !signatureDrawn}
            onClick={handleClear}
            dataTestId="signature-clear"
          />
        )}
      </div>
      <div
        ref={containerRef}
        className={clsx(
          "w-full h-[15rem] rounded-[var(--input-brand-radius,0.8rem)] border border-solid border-neutral-200 bg-neutral-20 overflow-hidden",
          !showDrawMode && "flex items-center justify-center p-small",
        )}
      >
        <Tooltip
          text={
            isReadonly
              ? getUITranslation("signatureEditingDisabled", selectedLanguage)
              : undefined
          }
          mountTo="body"
          className="pointer-events-auto"
          placement="top"
          offsetBy={10}
        >
          {showDrawMode ? (
            <SignatureCanvas
              ref={sigRef}
              clearOnResize={false}
              penColor="black"
              canvasProps={{
                id,
                width: canvasSize.width,
                height: canvasSize.height,
                className: clsx(
                  "block w-full max-w-full h-full",
                  isReadonly && "pointer-events-none",
                  disabled && "opacity-50",
                ),
                "aria-label":
                  label || getUITranslation("signature", selectedLanguage),
              }}
              onEnd={() => {
                if (isReadonly) return;
                onSignatureDrawDone();
              }}
            />
          ) : (
            <input
              id={id}
              type="text"
              autoFocus={mode === "type"}
              autoComplete="off"
              disabled={isReadonly}
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              onBlur={() => saveTypedSignature(typedSignature)}
              className={clsx(
                "w-full h-full bg-transparent border-0 outline-none text-center text-signature font-birthstone rounded-[var(--input-brand-radius,0.8rem)]",
                disabled && "opacity-50",
              )}
            />
          )}
        </Tooltip>
      </div>
    </div>
  );
};
